import { initializeApp } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence, RecaptchaVerifier, initializeRecaptchaConfig } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

// App Check → Web app → reCAPTCHA Enterprise site key (separate from Auth SMS Defense).
export const RECAPTCHA_ENTERPRISE_SITE_KEY = '6LcBjeorAAAAAO4Ubq2xe3GMnpg3QFJOIMPJBLLo';

const firebaseConfig = {
  apiKey: "AIzaSyDiffvoyal9yXawi59uX2TUDUyE3o2Zhq8",
  authDomain: "rocketexpresstt.com",
  projectId: "express-clean-auto-wash",
  storageBucket: "express-clean-auto-wash.firebasestorage.app",
  messagingSenderId: "805690671199",
  appId: "1:805690671199:web:dd3ec00d5028778dfd5bea"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Initialize App Check with reCAPTCHA Enterprise (for web)
const appCheck = initializeAppCheck(app, {
  provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_SITE_KEY),
  isTokenAutoRefreshEnabled: true
});

// Set authentication persistence to LOCAL (survives page refresh)
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    console.log('Authentication persistence set to LOCAL');
  })
  .catch((error) => {
    console.error('Error setting auth persistence:', error);
  });

const db = getFirestore(app);
const functions = getFunctions(app);

/**
 * Fetch Auth reCAPTCHA / SMS Defense config early.
 * Required so phone auth can use project-level SMS Defense (toll fraud protection)
 * instead of static high-risk carrier blocks (e.g. Digicel +1868).
 * Keep RecaptchaVerifier below for AUDIT-mode v2 fallback.
 */
export const recaptchaConfigReady = initializeRecaptchaConfig(auth)
  .then(() => {
    console.log('Auth reCAPTCHA / SMS Defense config loaded');
  })
  .catch((error) => {
    console.warn(
      'Auth reCAPTCHA config init failed (phone auth may fall back to v2 only):',
      error?.code || error?.message || error
    );
  });

let recaptchaWidgetSeq = 0;

/** Safely tear down a RecaptchaVerifier (clear can throw auth/internal-error). */
export const clearRecaptchaVerifier = (verifier) => {
  if (!verifier) return;
  try {
    verifier.clear();
  } catch (e) {
    console.warn('reCAPTCHA clear skipped:', e?.code || e?.message || e);
  }
};

/**
 * Create a fresh invisible RecaptchaVerifier for phone auth.
 * Used for AUDIT fallback (and when Enterprise assessment needs v2).
 * Unique child element each time avoids "already been rendered".
 */
export const setupRecaptcha = async (containerId = 'recaptcha-container') => {
  await recaptchaConfigReady;

  let host = document.getElementById(containerId);
  if (!host) {
    host = document.createElement('div');
    host.id = containerId;
    document.body.appendChild(host);
  }
  host.innerHTML = '';
  host.style.display = 'none';

  const widgetId = `recaptcha-widget-${++recaptchaWidgetSeq}`;
  const widgetEl = document.createElement('div');
  widgetEl.id = widgetId;
  host.appendChild(widgetEl);

  const recaptchaVerifier = new RecaptchaVerifier(auth, widgetId, {
    size: 'invisible',
    callback: () => {
      console.log('reCAPTCHA verified');
    },
    'expired-callback': () => {
      console.log('reCAPTCHA expired');
    },
    'error-callback': (error) => {
      console.log('reCAPTCHA error:', error);
    },
  });

  await recaptchaVerifier.render();
  return recaptchaVerifier;
};

/** Clear any existing verifier and return a brand-new one. */
export const resetRecaptcha = async (existingVerifier, containerId = 'recaptcha-container') => {
  clearRecaptchaVerifier(existingVerifier);
  const host = document.getElementById(containerId);
  if (host) host.innerHTML = '';
  await new Promise((r) => setTimeout(r, 200));
  return setupRecaptcha(containerId);
};

export { app, auth, db, functions, RecaptchaVerifier, appCheck };
